<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 transition-opacity duration-300 animate-modalFade"
  >
    <div class="bg-gray-800 rounded-lg w-full max-w-md overflow-hidden shadow-lg border border-gray-600 transform transition-all duration-300 animate-scaleIn">
      <!-- Header -->
      <div class="p-4 border-b border-gray-700 flex justify-between items-center bg-gray-800">
        <h2 class="text-lg font-semibold text-indigo-300">รางวัลประจำสัปดาห์ตึก {{ building || 'N/A' }}</h2>
        <button @click="$emit('close')" class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 group">
          <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
          <span class="tooltip">ปิด</span>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 overflow-y-auto max-h-96 scrollbar-thin">
        <div class="text-center text-indigo-200 text-sm mb-4 animate-fadeIn">
          รางวัลจะมอบในอีก {{ timeLeft }}
        </div>
        <div v-if="isLoadingTiers" class="text-center text-indigo-200 text-sm animate-fadeIn">
          กำลังโหลดข้อมูลรางวัล...
        </div>
        <div v-else-if="sortedTiers.length > 0" class="space-y-3 animate-fadeIn">
          <div
            v-for="(tier, index) in sortedTiers"
            :key="index"
            class="flex items-center gap-3 p-3 bg-gray-700 rounded-md cursor-pointer hover:bg-gray-600 transition-all duration-200 border border-gray-600"
            @click="showItemDetails(tier.itemName)"
          >
            <div class="flex-1">
              <p class="text-indigo-200 font-medium text-sm">
                อันดับ {{ tier.min }}{{ tier.min !== tier.max ? `-${tier.max}` : '' }}
              </p>
              <div class="flex items-center gap-2 text-indigo-300 text-xs">
                <span>จำนวน: {{ tier.quantity }}</span>
                <span>ไอเทม: {{ tier.itemName }}</span>
              </div>
            </div>
            <img
              v-if="tier.img"
              :src="tier.img"
              alt="ไอเทมรางวัล"
              class="w-10 h-10 object-cover rounded-full border border-gray-600"
            />
          </div>
        </div>
        <div v-else class="text-center text-red-300 text-sm animate-fadeIn">
          ไม่พบข้อมูลรางวัล
        </div>
      </div>

      <!-- Footer -->
      <div class="p-4 border-t border-gray-700 flex justify-end bg-gray-800">
        <button
          @click="$emit('close')"
          class="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors duration-200"
        >
          ปิด
        </button>
      </div>
    </div>

    <!-- Item Details Modal -->
    <div
      v-if="selectedItem"
      class="fixed inset-0 z-60 flex items-center justify-center bg-gray-900 bg-opacity-70 animate-modalFade"
      @click.self="selectedItem = null"
    >
      <div class="bg-gray-800 rounded-lg w-full max-w-md p-4 shadow-lg border border-gray-600 animate-scaleIn">
        <div class="flex justify-between items-center mb-3">
          <h3 class="text-base font-semibold text-indigo-300">{{ selectedItem }}</h3>
          <button @click="selectedItem = null" class="text-gray-300 hover:text-indigo-300 group">
            <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
            <span class="tooltip">ปิด</span>
          </button>
        </div>
        <div class="text-indigo-200 text-sm space-y-2">
          <div class="flex items-center gap-3">
            <img
              v-if="currentItemDetails.img"
              :src="currentItemDetails.img"
              :alt="selectedItem"
              class="w-10 h-10 object-cover rounded-full border border-gray-600"
            />
            <div>
              <div><span class="font-medium">ประเภท:</span> {{ currentItemDetails.type || 'N/A' }}</div>
            </div>
          </div>
          <div>
            <span class="font-medium">เลเวลขั้นต่ำ:</span> {{ currentItemDetails.requiredLevel || 1 }}
          </div>
          <div>
            <span class="font-medium">น้ำหนัก:</span> {{ currentItemDetails.weight || 0 }}
          </div>
          <div>
            <span class="font-medium">สวมใส่ได้:</span> {{ wearableTypes.includes(currentItemDetails.type) ? 'ใช่' : 'ไม่' }}
          </div>
          <div class="flex items-center gap-1">
            <span class="font-medium">ราคา:</span>
            <i v-if="currentItemDetails.currencyType === 'gold'" class="fa-solid fa-g text-yellow-400"></i>
            <i v-else-if="currentItemDetails.currencyType === 'diamond'" class="fa-solid fa-gem text-blue-400"></i>
            <i v-else class="fa-solid fa-coins text-gray-100"></i>
            {{ currentItemDetails.price || 0 }}
          </div>
          <div>
            <span class="font-medium">คำอธิบาย:</span> {{ currentItemDetails.description || 'ไม่มี' }}
          </div>
          <div v-if="hasStats">
            <span class="font-medium">Stats:</span>
            <ul class="list-disc ml-4 text-xs">
              <li v-if="itemStats.stamina > 0">Stamina: {{ itemStats.stamina }}</li>
              <li v-if="itemStats.str > 0">Strength: {{ itemStats.str }}</li>
              <li v-if="itemStats.int > 0">Intelligence: {{ itemStats.int }}</li>
              <li v-if="itemStats.agi > 0">Agility: {{ itemStats.agi }}</li>
              <li v-if="itemStats.maxHp > 0">Max HP: {{ itemStats.maxHp }}</li>
              <li v-if="itemStats.atk > 0">Attack: {{ itemStats.atk }}</li>
              <li v-if="itemStats.evasion > 0">Evasion: {{ itemStats.evasion }}</li>
              <li v-if="itemStats.critical > 0">Critical: {{ itemStats.critical }}</li>
              <li v-if="itemStats.defense > 0">Defense: {{ itemStats.defense }}</li>
            </ul>
          </div>
          <div v-if="currentItemDetails.type === 'รักษา' && currentItemDetails.healHpPercentage > 0">
            <span class="font-medium">HP ที่ได้รับ:</span> {{ currentItemDetails.healHpPercentage }}%
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { projectFirestore } from '@/firebase/config';
import gameItemsData from '@/data/gameItems.json';

export default {
  props: {
    building: {
      type: String,
      required: true,
      validator: (value) => ['1', '2'].includes(value)
    }
  },
  setup(props) {
    const isLoadingTiers = ref(true);
    const tiers = ref([]);
    const itemDetails = ref({});
    const timeLeft = ref('');
    const selectedItem = ref(null);

    const wearableTypes = [
      'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง',
      'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง',
      'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์'
    ];

    const calculateTimeLeft = () => {
      const now = new Date();
      const dayOfWeek = now.getDay();
      let daysToNextSunday = (7 - dayOfWeek) % 7;
      const nextSunday = new Date(now);
      nextSunday.setDate(now.getDate() + daysToNextSunday);
      nextSunday.setHours(21, 0, 0, 0);
      let diff = nextSunday - now;
      if (diff <= 0) {
        nextSunday.setDate(nextSunday.getDate() + 7);
        diff = nextSunday - now;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      timeLeft.value = `${days} วัน ${hours} ชั่วโมง ${minutes} นาที ${seconds} วินาที`;
    };

    const showItemDetails = (itemName) => {
      if (itemDetails.value[itemName]) {
        selectedItem.value = itemName;
      }
    };

    const sortedTiers = computed(() => {
      return [...tiers.value].sort((a, b) => a.min - b.min).map(tier => ({
        ...tier,
        img: itemDetails.value[tier.itemName]?.img,
        rarityColors: itemDetails.value[tier.itemName]?.rarityColors,
      }));
    });

    const currentItemDetails = computed(() => itemDetails.value[selectedItem.value] || {});

    const itemStats = computed(() => currentItemDetails.value.stats || {});

    const hasStats = computed(() => {
      return Object.values(itemStats.value).some(value => value > 0);
    });

    onMounted(async () => {
      calculateTimeLeft();
      setInterval(calculateTimeLeft, 1000);

      try {
        if (!props.building) {
          throw new Error('Building prop is undefined');
        }
        const docId = `weeklyRewardsBuilding${props.building}`;
        const weeklyDoc = await projectFirestore.collection('server').doc(docId).get();
        if (weeklyDoc.exists && weeklyDoc.data().tiers) {
          tiers.value = weeklyDoc.data().tiers;
        } else {
          tiers.value = [];
        }

        const uniqueItemNames = [...new Set(tiers.value.map(t => t.itemName))];
        uniqueItemNames.forEach((name) => {
          const itemData = gameItemsData[name];
          if (itemData) {
            itemDetails.value[name] = itemData;
          }
        });
      } catch (err) {
        console.error(`Error fetching weekly rewards for Building ${props.building || 'unknown'}:`, err);
        tiers.value = [];
      } finally {
        isLoadingTiers.value = false;
      }
    });

    return {
      isLoadingTiers,
      tiers,
      itemDetails,
      timeLeft,
      selectedItem,
      wearableTypes,
      sortedTiers,
      currentItemDetails,
      itemStats,
      hasStats,
      showItemDetails,
      building: props.building // Expose building to the template
    };
  }
};
</script>

<style scoped>
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Animations */
@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

/* Tooltip */
.tooltip {
  visibility: hidden;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: #e0e7ff;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  margin-bottom: 0.5rem;
}

button:hover .tooltip {
  visibility: visible;
  opacity: 1;
}
</style>